import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Collapse, Divider, Grid,
  Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import T from '../common/T';
import userApi from '../../api/userApi';
import tagApi from '../../api/tagApi';
import {
  askForCapability, getAccessRequests, getEffectivePermissions, withdrawAccessRequest,
} from '../../api/scopedPermissionApi';
import { useAuth } from './AuthContext';

const BRAND = '#32cd32';

/**
 * "My access" — what this person may actually do, and where.
 *
 * The old version of this screen invented a list of permission names in the
 * browser from the role in the token. None of those names existed in the
 * permission catalogue the services check, so the page showed rights nobody
 * had and hid the ones they did have.
 *
 * This version shows the truth and nothing else:
 *   - the identity and the roles/tags it comes from, read from the server,
 *   - every capability the person holds, with its real key and its label,
 *   - where a capability is limited (workshop, model, customer, …) and until
 *     when, from the scoped grants (ADR-0034),
 *   - what exists but is not granted, so a person can ask for it by name.
 */
const UserPermissions = () => {
  const { user: currentUser } = useAuth();
  const [identity, setIdentity] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openCategories, setOpenCategories] = useState({});
  const [requests, setRequests] = useState([]);
  const [asking, setAsking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const me = await userApi.getCurrentUser();
      setIdentity(me);
      const available = await tagApi.getAvailablePermissions();
      setCatalogue(available?.permissions || []);
      try {
        setRequests(await getAccessRequests('pending'));
      } catch {
        setRequests([]);
      }
      if (me?.actor_id) {
        // A person may always read their own rights; a failure here must not
        // hide the rest of the page.
        try {
          setGrants(await getEffectivePermissions(me.actor_id));
        } catch {
          setGrants([]);
        }
      }
    } catch (requestError) {
      setError(requestError?.message || 'Your access could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // An administrator passes every check in every service, whatever the token
  // happens to list. Showing them a near-empty page would be a lie.
  const isAdmin = identity?.is_admin === true;
  const held = useMemo(() => {
    if (isAdmin) return new Set((catalogue || []).map((entry) => String(entry.key).toLowerCase()));
    return new Set((identity?.permissions || []).map((key) => String(key).toLowerCase()));
  }, [catalogue, identity, isAdmin]);

  const limitsByCapability = useMemo(() => {
    const map = {};
    (grants || []).forEach((grant) => {
      if (!grant?.capability) return;
      const key = String(grant.capability).toLowerCase();
      (map[key] = map[key] || []).push(grant);
    });
    return map;
  }, [grants]);

  const groups = useMemo(() => {
    const byCategory = {};
    (catalogue || []).forEach((entry) => {
      const category = entry.category || 'General';
      byCategory[category] = byCategory[category] || { granted: [], missing: [] };
      const key = String(entry.key).toLowerCase();
      (held.has(key) ? byCategory[category].granted : byCategory[category].missing).push(entry);
    });
    // Areas where the person has something come first, then alphabetically,
    // so the page reads the same way every time.
    return Object.entries(byCategory).sort((a, b) => {
      const mine = (b[1].granted.length > 0 ? 1 : 0) - (a[1].granted.length > 0 ? 1 : 0);
      return mine !== 0 ? mine : a[0].localeCompare(b[0]);
    });
  }, [catalogue, held]);

  // Keys the person holds that the catalogue does not describe — for example
  // the older `location_filter:…` entries. They are shown, never hidden.
  const uncatalogued = useMemo(() => {
    const known = new Set((catalogue || []).map((entry) => String(entry.key).toLowerCase()));
    return (identity?.permissions || []).filter((key) => !known.has(String(key).toLowerCase()));
  }, [catalogue, identity]);

  const limited = useMemo(
    () => (grants || []).filter((grant) => (grant.scopes || []).length > 0 || grant.validUntil),
    [grants],
  );

  const grantedCount = groups.reduce((total, [, value]) => total + value.granted.length, 0);
  const pendingFor = (key) => requests.find(
    (request) => String(request.capability).toLowerCase() === String(key).toLowerCase(),
  );

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 10 }}>
        <CircularProgress sx={{ color: BRAND }} />
      </Stack>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} gap={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}><T>My access</T></Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            <T>What you may do in the ERP, where it comes from, and where it is limited.</T>
          </Typography>
        </Box>
        <Button onClick={load} startIcon={<RefreshIcon />} sx={{ color: BRAND, fontWeight: 700 }}>
          <T>Refresh</T>
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <T>You are an administrator, so every capability below is open to you.</T>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={currentUser?.profile_picture_url || undefined}
              sx={{ width: 64, height: 64, bgcolor: BRAND, fontSize: 26, fontWeight: 800 }}
            >
              {(identity?.email || '?').charAt(0).toUpperCase()}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              {currentUser?.name || identity?.email}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>{identity?.email}</Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
              {identity?.is_admin && (
                <Chip size="small" label="Administrator"
                      sx={{ fontWeight: 700, bgcolor: '#fee2e2', color: '#991b1b' }} />
              )}
              {(identity?.tags || identity?.roles || []).map((tag) => (
                <Chip key={tag} size="small" icon={<SellOutlinedIcon sx={{ fontSize: 15 }} />} label={tag}
                      sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
              ))}
            </Stack>
          </Grid>
          <Grid item>
            <Stack gap={0.5} sx={{ textAlign: { md: 'right' } }}>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                <T>Capabilities</T>: <strong>{grantedCount}</strong>
              </Typography>
              {limited.length > 0 && (
                <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700 }}>
                  {limited.length} <T>limited</T>
                </Typography>
              )}
              {identity?.last_login && (
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  <T>Last sign-in</T>: {new Date(identity.last_login).toLocaleString('de-CH')}
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {limited.length > 0 && (
        <Paper variant="outlined"
               sx={{ p: 2.5, borderRadius: 3, mb: 3, borderColor: '#fcd34d', bgcolor: '#fffbeb' }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <LockOutlinedIcon sx={{ color: '#b45309' }} />
            <Typography sx={{ fontWeight: 800, color: '#92400e' }}>
              <T>Some of your rights apply only in certain places</T>
            </Typography>
          </Stack>
          <Stack gap={1}>
            {limited.map((grant) => (
              <Stack key={`${grant.capability}-${grant.source}`} direction="row" gap={1.5}
                     alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{grant.capability}</Typography>
                {(grant.scopes || []).map((scope) => (
                  <Chip key={`${scope.scope_type}-${scope.scope_value}`} size="small"
                        icon={<PlaceOutlinedIcon sx={{ fontSize: 15 }} />}
                        label={scope.label || scope.scope_value}
                        sx={{ bgcolor: 'white', border: '1px solid #fcd34d' }} />
                ))}
                {grant.validUntil && (
                  <Chip size="small" icon={<ScheduleOutlinedIcon sx={{ fontSize: 15 }} />}
                        label={new Date(grant.validUntil).toLocaleDateString('de-CH')}
                        sx={{ bgcolor: 'white', border: '1px solid #fcd34d' }} />
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {groups.map(([category, value]) => (
        <Paper key={category} variant="outlined" sx={{ borderRadius: 3, mb: 2, overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between"
                 sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontWeight: 800 }}>{category}</Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {value.granted.length}/{value.granted.length + value.missing.length}
            </Typography>
          </Stack>

          {value.granted.length === 0 && (
            <Typography variant="body2" sx={{ p: 2.5, color: '#94a3b8' }}>
              <T>Nothing granted in this area.</T>
            </Typography>
          )}

          {value.granted.map((entry) => {
            const limits = limitsByCapability[String(entry.key).toLowerCase()] || [];
            const scoped = limits.filter((grant) => (grant.scopes || []).length > 0 || grant.validUntil);
            const source = limits.find((grant) => String(grant.source || '').startsWith('tag:'));
            return (
              <Stack key={entry.key} direction="row" alignItems="center" gap={1.5}
                     sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid #f1f5f9' }}>
                <CheckCircleOutlineIcon sx={{ color: BRAND, fontSize: 20 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>{entry.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>{entry.key}</Typography>
                </Box>
                {scoped.map((grant) => (
                  (grant.scopes || []).map((scope) => (
                    <Chip key={`${grant.source}-${scope.scope_value}`} size="small"
                          icon={<PlaceOutlinedIcon sx={{ fontSize: 14 }} />}
                          label={scope.label || scope.scope_value}
                          sx={{ bgcolor: '#fffbeb', border: '1px solid #fcd34d', fontWeight: 600 }} />
                  ))
                ))}
                {isAdmin && !source ? (
                  <Chip size="small" label={<T>Administrator</T>}
                        sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
                ) : (
                  <Tooltip title={source ? source.source.replace('tag:', '') : 'Role'}>
                    <Chip size="small" label={source ? source.source.replace('tag:', '') : <T>Role</T>}
                          sx={{ bgcolor: '#f1f5f9', fontWeight: 600, maxWidth: 160 }} />
                  </Tooltip>
                )}
              </Stack>
            );
          })}

          {value.missing.length > 0 && (
            <>
              <Button
                size="small"
                onClick={() => setOpenCategories({
                  ...openCategories, [category]: !openCategories[category],
                })}
                endIcon={openCategories[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ m: 1.5, color: '#64748b', fontWeight: 600 }}
              >
                {value.missing.length} <T>not granted</T>
              </Button>
              <Collapse in={!!openCategories[category]}>
                <Divider />
                {value.missing.map((entry) => {
                  const pending = pendingFor(entry.key);
                  return (
                    <Stack key={entry.key} direction="row" alignItems="center" gap={1.5}
                           sx={{ px: 2.5, py: 1 }}>
                      <LockOutlinedIcon sx={{ fontSize: 18, color: '#cbd5e1' }} />
                      <Box sx={{ flex: 1, minWidth: 0, opacity: 0.65 }}>
                        <Typography sx={{ fontSize: '0.9rem' }}>{entry.label}</Typography>
                        <Typography variant="caption" sx={{ color: '#cbd5e1' }}>{entry.key}</Typography>
                      </Box>
                      {pending ? (
                        <Chip size="small" icon={<HourglassEmptyIcon sx={{ fontSize: 15 }} />}
                              label={<T>Requested</T>} onDelete={async () => {
                                await withdrawAccessRequest(pending.id);
                                setRequests(requests.filter((row) => row.id !== pending.id));
                              }}
                              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
                      ) : (
                        <Button size="small" onClick={() => setAsking(entry)}
                                sx={{ color: BRAND, fontWeight: 700 }}>
                          <T>Request</T>
                        </Button>
                      )}
                    </Stack>
                  );
                })}
              </Collapse>
            </>
          )}
        </Paper>
      ))}

      {uncatalogued.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5 }}>
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}><T>Other entries on your account</T></Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            <T>Older entries that are not part of the permission catalogue.</T>
          </Typography>
          <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
            {uncatalogued.map((key) => (
              <Chip key={key} size="small" label={key} sx={{ bgcolor: '#f1f5f9' }} />
            ))}
          </Stack>
        </Paper>
      )}
      <AskDialog
        entry={asking}
        onClose={() => setAsking(null)}
        onAsked={(request) => { setRequests([...requests, request]); setAsking(null); }}
      />
    </Box>
  );
};

/** Ask an administrator for one capability, with a reason they can act on. */
const AskDialog = ({ entry, onClose, onAsked }) => {
  const [reason, setReason] = useState('');
  const [where, setWhere] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setReason(''); setWhere(''); setError(''); }, [entry]);

  const send = async () => {
    setSaving(true);
    setError('');
    try {
      onAsked(await askForCapability(entry.key, reason, where));
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'The request could not be sent.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!entry} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        <T>Request access</T>{entry ? `: ${entry.label}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            <T>An administrator decides. Nothing changes until they approve it.</T>
          </Typography>
          <TextField
            label={<T>Why do you need it?</T>}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline minRows={2} inputProps={{ maxLength: 500 }}
          />
          <TextField
            label={<T>Where do you need it? (optional)</T>}
            value={where}
            onChange={(event) => setWhere(event.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText={<T>For example one workshop, one customer or one repair.</T>}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}><T>Cancel</T></Button>
        <Button variant="contained" onClick={send} disabled={saving || !reason.trim()}
                sx={{ bgcolor: BRAND, fontWeight: 700, '&:hover': { bgcolor: '#28a428' } }}>
          <T>Send request</T>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AskDialog.propTypes = {
  entry: PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
  onAsked: PropTypes.func.isRequired,
};

AskDialog.defaultProps = { entry: null };

export default UserPermissions;
